import { useEffect, useMemo } from "react";
import debounce from "lodash/debounce";

// Recherche d'adresse via Places API (New) : suggestions pendant la saisie puis
// un seul appel de détail à la sélection. Le session token regroupe les deux
// pour la facturation (suggestions gratuites, détail en catégorie Essentials).
// Ne demander que des champs Essentials : en ajouter d'autres change le tarif.

const MIN_INPUT_LENGTH = 3;
const DEBOUNCE_MS = 300;
const PLACE_FIELDS = ["formattedAddress", "location"];

// Typage minimal de Places API (New), indépendant de la version de
// @types/google.maps installée
type SessionToken = object;

type PlacePrediction = {
  text: { toString(): string };
  toPlace(): {
    formattedAddress?: string | null;
    location?: google.maps.LatLng | null;
    fetchFields(options: { fields: string[] }): Promise<unknown>;
  };
};

type PlacesLibrary = {
  AutocompleteSessionToken: new () => SessionToken;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions(request: {
      input: string;
      sessionToken: SessionToken;
    }): Promise<{ suggestions: { placePrediction: PlacePrediction | null }[] }>;
  };
};

// Charge la librairie places quel que soit le chargement du script Google
const loadPlaces = () =>
  (google.maps as any).importLibrary("places") as Promise<PlacesLibrary>;

export type AddressOption = {
  label: string;
  value: string;
  placePrediction?: PlacePrediction;
};

export type ResolvedAddress = {
  address: string;
  location: google.maps.LatLng;
};

function useAddressSearch() {
  const search = useMemo(() => {
    let sessionToken: SessionToken | null = null;

    const fetchOptions = async (input: string): Promise<AddressOption[]> => {
      const places = await loadPlaces();
      sessionToken ??= new places.AutocompleteSessionToken();
      const { suggestions } =
        await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken,
        });

      return suggestions.flatMap(({ placePrediction }) => {
        if (!placePrediction) return [];
        const label = placePrediction.text.toString();
        return [{ label, value: label, placePrediction }];
      });
    };

    const loadOptions = debounce(
      (input: string, callback: (options: AddressOption[]) => void) => {
        if (input.trim().length < MIN_INPUT_LENGTH) {
          callback([]);
          return;
        }
        fetchOptions(input)
          .then(callback)
          .catch((e) => {
            console.error(e);
            callback([]);
          });
      },
      DEBOUNCE_MS
    );

    // Termine la session : le token est transmis automatiquement par toPlace()
    const resolveAddress = async (
      option: AddressOption
    ): Promise<ResolvedAddress | null> => {
      if (!option.placePrediction) return null;
      const place = option.placePrediction.toPlace();
      sessionToken = null;
      await place.fetchFields({ fields: PLACE_FIELDS });
      if (!place.location) return null;
      return {
        address: place.formattedAddress ?? option.label,
        location: place.location,
      };
    };

    return { loadOptions, resolveAddress };
  }, []);

  useEffect(() => () => search.loadOptions.cancel(), [search]);

  return search;
}

export default useAddressSearch;
